import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { environment } from '@env/environment';

/** Total animation duration (visible + fade) for HUD rows, in ms. */
const DEFAULT_FADE_MS = 4000;
const MIN_FADE_MS = 500;
const MAX_FADE_MS = 60_000;

/** Default dev-bar height (px) for the expanded view. */
const DEFAULT_HEIGHT_PX = 280;
const MIN_HEIGHT_PX = 120;

@Injectable({ providedIn: 'root' })
export class DevBottomBarService {
  readonly networkVisible$ = new BehaviorSubject<boolean>(
    !environment.production && localStorage.getItem('devBar.network') === 'true'
  );
  readonly consoleVisible$ = new BehaviorSubject<boolean>(
    !environment.production && localStorage.getItem('devBar.console') === 'true'
  );
  /**
   * Minimized HUD mode. Defaults to `true` when the key is absent so first-time
   * users get the non-intrusive overlay instead of the full panel.
   */
  readonly minimized$ = new BehaviorSubject<boolean>(
    !environment.production && (localStorage.getItem('devBar.minimized') ?? 'true') !== 'false'
  );

  /** HUD fade duration (ms) for network rows. */
  readonly fadeDurationNetworkMs$ = new BehaviorSubject<number>(
    this.readPersistedDuration('devBar.fadeDurationNetworkMs', DEFAULT_FADE_MS)
  );
  /** HUD fade duration (ms) for console rows. */
  readonly fadeDurationConsoleMs$ = new BehaviorSubject<number>(
    this.readPersistedDuration('devBar.fadeDurationConsoleMs', DEFAULT_FADE_MS)
  );

  /** User-configured expanded height (px). Resized by dragging the top edge. */
  readonly heightPx$ = new BehaviorSubject<number>(
    this.readPersistedHeight('devBar.heightPx', DEFAULT_HEIGHT_PX)
  );

  readonly anyVisible$: Observable<boolean> = combineLatest([
    this.networkVisible$,
    this.consoleVisible$,
  ]).pipe(map(([n, c]) => n || c));

  constructor() {
    if (environment.production) return;

    this.networkVisible$.subscribe(v => localStorage.setItem('devBar.network', String(v)));
    this.consoleVisible$.subscribe(v => localStorage.setItem('devBar.console', String(v)));
    this.minimized$.subscribe(v => localStorage.setItem('devBar.minimized', String(v)));
    this.fadeDurationNetworkMs$.subscribe(v =>
      localStorage.setItem('devBar.fadeDurationNetworkMs', String(v))
    );
    this.fadeDurationConsoleMs$.subscribe(v =>
      localStorage.setItem('devBar.fadeDurationConsoleMs', String(v))
    );
    this.heightPx$.subscribe(v => localStorage.setItem('devBar.heightPx', String(v)));

    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'n') {
        e.preventDefault();
        this.networkVisible$.next(!this.networkVisible$.value);
      }
      if (e.ctrlKey && e.key === 'd') {
        e.preventDefault();
        this.consoleVisible$.next(!this.consoleVisible$.value);
      }
    });
  }

  toggleNetwork(): void { this.networkVisible$.next(!this.networkVisible$.value); }
  toggleConsole(): void { this.consoleVisible$.next(!this.consoleVisible$.value); }
  toggleMinimized(): void { this.minimized$.next(!this.minimized$.value); }

  setFadeDurationNetworkMs(ms: number): void {
    this.fadeDurationNetworkMs$.next(this.clampDuration(ms, DEFAULT_FADE_MS));
  }

  setFadeDurationConsoleMs(ms: number): void {
    this.fadeDurationConsoleMs$.next(this.clampDuration(ms, DEFAULT_FADE_MS));
  }

  setHeightPx(px: number): void {
    this.heightPx$.next(this.clampHeight(px));
  }

  private readPersistedDuration(key: string, fallback: number): number {
    if (environment.production) return fallback;
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed = Number(raw);
    return this.clampDuration(parsed, fallback);
  }

  private clampDuration(value: number, fallback: number): number {
    if (!Number.isFinite(value) || value <= 0) return fallback;
    return Math.min(MAX_FADE_MS, Math.max(MIN_FADE_MS, Math.round(value)));
  }

  private readPersistedHeight(key: string, fallback: number): number {
    if (environment.production) return fallback;
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return fallback;
    return this.clampHeight(parsed);
  }

  private clampHeight(value: number): number {
    if (!Number.isFinite(value)) return DEFAULT_HEIGHT_PX;
    // Upper bound computed at call time so it tracks viewport changes.
    const max = typeof window !== 'undefined'
      ? Math.max(MIN_HEIGHT_PX, Math.floor(window.innerHeight * 0.9))
      : 2000;
    return Math.min(max, Math.max(MIN_HEIGHT_PX, Math.round(value)));
  }
}
